import { JUHUU } from "@juhuu/sdk-ts";
import type { Product } from "../lib/types";
import { useCallback, useEffect, useState } from "react";
import { juhuu } from "../juhuuClass";

// Define category colors
const categoryColors: Record<string, { bg: string; text: string }> = {
  "Mobility Solutions": { bg: "bg-blue-100", text: "text-blue-800" },
  "Smart Infrastructure": { bg: "bg-green-100", text: "text-green-800" },
  "Digital Twins": { bg: "bg-purple-100", text: "text-purple-800" },
  "Data Analytics": { bg: "bg-amber-100", text: "text-amber-800" },
  "User Access": { bg: "bg-red-100", text: "text-red-800" },
  "Booking Systems": { bg: "bg-cyan-100", text: "text-cyan-800" },
  "Payment Solutions": { bg: "bg-emerald-100", text: "text-emerald-800" },
  "Integration Tools": { bg: "bg-indigo-100", text: "text-indigo-800" },
};

interface ProductCardProps {
  product: JUHUU.Product.Object;
}

export default function ProductCard({ product }: ProductCardProps) {
  // Get category colors or default to gray
  const [property, setProperty] = useState<JUHUU.Property.Object | null>(null);

  const handleRefresh = useCallback(async () => {
    const query = await juhuu.properties.retrieve({
      propertyId: product.propertyId,
    });

    if (query.ok === false) {
      return;
    }

    setProperty(query.data.property);
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  return (
    <a href={`/marketplace/${product.id}`}>
      <div className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col transform hover:-translate-y-1">
        {/* Category Badge - More prominent */}
        <div className="absolute top-3 left-3 z-10">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium`}
          ></span>
        </div>

        {/* Product img */}
        <div className="aspect-w-16 aspect-h-9 bg-gray-200 group-hover:opacity-90 transition-opacity duration-300">
          <img
            src={product.bannerImageLight[0] || "/placeholder.svg"}
            alt={product.name}
            width={400}
            height={300}
            className="w-full h-56 object-cover"
          />
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col flex-grow">
          {/* Product Name */}
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            {product.name}
          </h3>

          {/* Short Description */}
          <p className="text-base text-gray-600 mb-6 line-clamp-3 flex-grow">
            {product.description.en}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {product.purposeArray.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
              >
                {tag.category?.toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        {/* Manufacturer Logo - Bottom */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 mt-auto">
          <div className="text-xs text-gray-500 mb-1.5">Powered by</div>
          <div className="h-7">
            <img
              src={property?.bannerImageLight ?? ""}
              width={120}
              height={30}
              className="h-full w-auto object-contain"
            />
          </div>
        </div>
      </div>
    </a>
  );
}
